import React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';



const fixedWidth = (width: number) => ({

  width,

  minWidth: width,

  maxWidth: width,

  boxSizing: 'border-box',

});



type HeaderCell = [string, number];



interface ActionButton<T extends object> {

  icon: React.ReactNode;

  onClick: (row: T) => void;

  condition?: (row: T) => boolean;

}



interface ListTableProps<T extends object> {

  headerCells: HeaderCell[];

  rows: T[];

  isLoading?: boolean;

  noDataText?: string;

  actions?: ActionButton<T>[];

  renderCell?: (row: T, columnIndex: number) => React.ReactNode;

  showCheckbox?: boolean;

  selectedIds?: string[];

  onSelectionChange?: (ids: string[]) => void;

  getRowId?: (row: T) => string;

}



// ColGroup ensures perfect width alignment for header & body

const ColGroup = ({
  headerCells,
  showCheckbox,
}: {
  headerCells: [string, number][];
  showCheckbox?: boolean;
}) => (

  <colgroup>

    {showCheckbox && (
      <col style={{ width: '40px', minWidth: '40px', maxWidth: '40px' }} />
    )}

    {headerCells.map(([_, width], i) => (

      <col

        key={i}

        style={{

          width: `${width}px`,

          minWidth: `${width}px`,

          maxWidth: `${width}px`,

        }}

      />

    ))}

  </colgroup>

);



export const ListTable = <T extends object>({

  headerCells,

  rows,

  isLoading = false,

  noDataText = 'No data found',

  actions,

  renderCell,

  showCheckbox = false,

  selectedIds = [],

  onSelectionChange,

  getRowId = (row: T) => (row as any)._id,

}: ListTableProps<T>) => {

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    onSelectionChange(checked ? rows.map((row) => getRowId(row)) : []);
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      checked ? [...selectedIds, id] : selectedIds.filter((i) => i !== id),
    );
  };

  const isAllSelected = rows.length > 0 && selectedIds.length === rows.length;
  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < rows.length;

  return (

    <TableContainer

      sx={{

        overflow: 'hidden',

        height: '100%',

        minHeight: 0,

        display: 'flex',

        flexDirection: 'column',

        borderTopRightRadius: 10,

        borderTopLeftRadius: 10,

      }}

    >

      <Box sx={{ flexShrink: 0 }}>

        <Table

          sx={{

            tableLayout: 'fixed',

            width: '100%',

            borderCollapse: 'separate',

            borderSpacing: '0 5px',

          }}

        >

          <ColGroup headerCells={headerCells} showCheckbox={showCheckbox} />

          <TableHead>

            <TableRow>

              {showCheckbox && (
                <TableCell
                  sx={{
                    textAlign: 'center',
                    color: '#FFFFFF',
                    backgroundColor: '#1C1C1C',
                    padding: '16px 4px',
                    borderTop: '1px solid #8A8D91',
                    borderBottom: '1px solid #8A8D91',
                    borderLeft: '1px solid #8A8D91',
                    borderTopLeftRadius: 10,
                    borderBottomLeftRadius: 10,
                    width: '40px',
                    minWidth: '40px',
                    maxWidth: '40px',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    sx={{
                      color: '#8A8D91',
                      '&.Mui-checked': { color: '#7B1FA2' },
                      '&.MuiCheckbox-indeterminate': { color: '#7B1FA2' },
                      p: 0,
                    }}
                  />
                </TableCell>
              )}

              {headerCells.map(([label, width], index) => (

                <TableCell

                  key={index}

                  sx={{

                    textAlign: 'center',

                    color: '#FFFFFF',

                    backgroundColor: '#1C1C1C',

                    ...fixedWidth(width),

                    padding: '25px 0px',

                    borderTop: '1px solid #8A8D91',

                    borderBottom: '1px solid #8A8D91',

                    borderLeft: !showCheckbox && index === 0 ? '1px solid #8A8D91' : 'none',

                    borderRight:

                      index === headerCells.length - 1

                        ? '1px solid #8A8D91'

                        : 'none',

                    borderTopLeftRadius: !showCheckbox && index === 0 ? 10 : 0,

                    borderBottomLeftRadius: !showCheckbox && index === 0 ? 10 : 0,

                    borderTopRightRadius:

                      index === headerCells.length - 1 ? 10 : 0,

                    borderBottomRightRadius:

                      index === headerCells.length - 1 ? 10 : 0,

                  }}

                >

                  {label === 'Action' && actions && actions.length > 0

                    ? 'Action'

                    : label}

                </TableCell>

              ))}

            </TableRow>

          </TableHead>

        </Table>

      </Box>



      <Box

        sx={{

          flex: '1 1 auto',

          minHeight: 0,

          overflowY: 'auto',

          scrollbarWidth: 'none',

        }}

      >

        <Table

          sx={{

            tableLayout: 'fixed',

            width: '100%',

            borderCollapse: 'separate',

            borderSpacing: '0 8px',

          }}

        >

          <ColGroup headerCells={headerCells} showCheckbox={showCheckbox} />

          <TableBody>

            {isLoading ? (

              <TableRow>

                <TableCell colSpan={headerCells.length + (showCheckbox ? 1 : 0)} align="center">

                  Loading...

                </TableCell>

              </TableRow>

            ) : rows.length === 0 ? (

              <TableRow>

                <TableCell colSpan={headerCells.length + (showCheckbox ? 1 : 0)} align="center">

                  {noDataText}

                </TableCell>

              </TableRow>

            ) : (

              rows.map((row, rowIndex) => (

                <TableRow

                  key={rowIndex}

                  sx={{

                    backgroundColor: '#1C1C1C',



                    '& > td': {

                      borderTop: '1px solid #3E3E3E',

                      borderBottom: '1px solid #3E3E3E',

                      color: '#8A8D91',

                      paddingY: '10px',

                      textAlign: 'center',

                      backgroundColor: '#1C1C1C',

                    },



                    '& > td:first-of-type': {

                      borderLeft: '1px solid #3E3E3E',

                      borderTopLeftRadius: 12,

                      borderBottomLeftRadius: 12,

                    },



                    '& > td:last-of-type': {

                      borderRight: '1px solid #3E3E3E',

                      borderTopRightRadius: 12,

                      borderBottomRightRadius: 12,

                    },

                  }}

                >

                  {showCheckbox && (
                    <TableCell
                      sx={{
                        textAlign: 'center',
                        padding: '8px 4px',
                        width: '40px',
                        minWidth: '40px',
                        maxWidth: '40px',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(getRowId(row))}
                        onChange={(e) =>
                          handleSelectOne(getRowId(row), e.target.checked)
                        }
                        sx={{
                          color: '#8A8D91',
                          '&.Mui-checked': { color: '#7B1FA2' },
                          p: 0,
                        }}
                      />
                    </TableCell>
                  )}

                  {headerCells.map(([label, width], colIdx) => (

                    <TableCell

                      key={colIdx}

                      sx={{

                        ...fixedWidth(width),

                        textAlign: 'center',

                      }}

                    >

                      {label === 'Action' && actions && actions.length > 0 ? (
                        (() => {
                          const visibleActions = actions.filter(
                            (action) =>
                              !action.condition || action.condition(row),
                          );

                          if (visibleActions.length === 0) {
                            return (
                              <Typography variant="body2" color="#888">
                                -
                              </Typography>
                            );
                          }

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 0,
                              }}
                            >
                              {visibleActions.map((action, i) => (
                                <IconButton
                                  key={i}
                                  sx={{ color: '#7B1FA2' }}
                                  onClick={() => action.onClick(row)}
                                >
                                  {action.icon}
                                </IconButton>
                              ))}
                            </Box>
                          );
                        })()
                      ) : renderCell ? (

                        renderCell(row, colIdx)

                      ) : (

                        String(row[Object.keys(row)[colIdx] as keyof T] ?? '')

                      )}

                    </TableCell>

                  ))}

                </TableRow>

              ))

            )}

          </TableBody>

        </Table>

      </Box>

    </TableContainer>

  );

};


